import { cva, type VariantProps } from 'class-variance-authority';
import { Plus } from 'lucide-react';
import type { ComponentProps } from 'react';

import Image from '@vs-shop/image-link/Image';
import { Checkbox } from '@vs-shop/ui/checkbox';
import { headingVariants } from '@vs-shop/ui/heading';
import { clsxm, type ClassValue } from '@vs-shop/utils';

import type { VsImageProps } from '@vs-shop/headless/useVsImage';

import { optionalClassName } from '../../internal/optionalClassName.ts';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * What a product is bought together with, as one offer a shopper takes in or
 * out a line at a time.
 *
 *   <ProductBundle>
 *     <ProductBundleTitle render={<Heading level={2} />}>{title}</ProductBundleTitle>
 *
 *     <ProductBundleList>
 *       {items.map((item, index) => (
 *         <ProductBundleLine key={item.code}>
 *           <ProductBundleCard>
 *             <ProductBundleCardBody selected={…} disabled={false}>…</ProductBundleCardBody>
 *           </ProductBundleCard>
 *           <ProductBundleJoin variant={…} />
 *         </ProductBundleLine>
 *       ))}
 *
 *       <ProductBundleSummary>
 *         <ProductBundleSummaryStack>
 *           <ProductBundleSummaryLines>
 *             <ProductBundleSummaryNote>…</ProductBundleSummaryNote>
 *             <ProductBundleSummaryTotal>…</ProductBundleSummaryTotal>
 *           </ProductBundleSummaryLines>
 *           <ProductAddToCart variant={ProductAddToCartVariant.SLIM} … />
 *         </ProductBundleSummaryStack>
 *       </ProductBundleSummary>
 *     </ProductBundleList>
 *   </ProductBundle>
 *
 * The way into the cart is `@vs-shop/commerce-ui/productAddToCart` — the same
 * part the buy box and the suggestion rows are, at the appearance that closes a
 * column. This section frames it and never reimplements it, and the mutation,
 * the prices, the stock arithmetic and the copy all stay the tenant's.
 *
 * No part renders a heading; the title is a neutral element the page decides.
 */

/**
 * The offer: a title over the line of things it is made of.
 *
 * The room it takes above and below is the section's, because a bundle sits
 * between two unrelated blocks of a product page and has to separate itself
 * from both.
 */
const productBundleClasses = 'lap:gap-8 lap:py-16 flex flex-col gap-6 py-6';

export type ProductBundleProps = ComponentProps<'div'>;

/** The bundle. */
export const ProductBundle = ({ className, ...props }: ProductBundleProps) => (
  <div data-slot="product-bundle" className={clsxm(productBundleClasses, className)} {...props} />
);

/**
 * What the offer is called.
 *
 * The scale comes from `@vs-shop/ui`'s heading primitive at its second level
 * rather than being restated, so a page supplying `render={<Heading level={2} />}`
 * renders one scale rather than two fighting over the same element.
 */
const productBundleTitleClasses = headingVariants({ level: 2 });

export type ProductBundleTitleProps = ComponentProps<'div'> & {
  /** The element to paint instead of the default `<div>` — the page's choice. */
  readonly render?: RenderElement;
};

/** The bundle's title. */
export const ProductBundleTitle = ({ className, render, ...props }: ProductBundleTitleProps) => (
  <RenderedPart
    dataSlot="product-bundle-title"
    className={clsxm(productBundleTitleClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * The line of things the offer is made of, with what it comes to at the end.
 *
 * A grid of two, then three, then one row: the equation only reads as an
 * equation when it runs across, and there is no width below `lap` where four
 * cards and a total would fit on one line. The two widths a line takes are
 * declared here as custom properties rather than repeated on every line,
 * because they are the section's measurements and every line has to agree on
 * them.
 */
const productBundleListClasses =
  'tab:grid-cols-3 tab:gap-x-6 tab:gap-y-6 lap:flex lap:gap-x-0 ' +
  'grid grid-cols-2 gap-x-4 gap-y-4 [--item-max-w:350px] [--item-min-w:135px]';

export type ProductBundleListProps = ComponentProps<'div'>;

/** The row of items and the summary. */
export const ProductBundleList = ({ className, ...props }: ProductBundleListProps) => (
  <div
    data-slot="product-bundle-list"
    className={clsxm(productBundleListClasses, className)}
    {...props}
  />
);

/**
 * One thing in the offer, and the sign that joins it to the next.
 *
 * Two columns from `lap` up — the card and the sign beside it — and one below
 * it, where the signs are not shown at all. The sign keeps its column whether
 * or not it has one to draw, so every card in the row is the same width.
 */
const productBundleLineClasses =
  'lap:max-w-[var(--item-max-w)] lap:min-w-[var(--item-min-w)] lap:grid-cols-[1fr_40px] ' +
  'lap-lg:grid-cols-[1fr_48px] grid w-full';

export type ProductBundleLineProps = ComponentProps<'div'>;

/** A line of the offer. */
export const ProductBundleLine = ({ className, ...props }: ProductBundleLineProps) => (
  <div
    data-slot="product-bundle-line"
    className={clsxm(productBundleLineClasses, className)}
    {...props}
  />
);

/**
 * The sign between two things in the offer.
 *
 * It renders its own glyph rather than taking one: what joins two items of a
 * bundle is a plus in every brand, and handing it in would put the same import
 * in every tenant. What IS a decision is whether this line has a next one, and
 * that is the appearance below.
 *
 * The blank appearance still takes its column. A line that dropped the sign
 * would be a card one column wider than its neighbours, so the last item's sign
 * is drawn in nothing rather than left out.
 */
const productBundleJoinClasses = 'lap:flex lap:w-10 lap-lg:w-12 hidden items-center justify-center';

/** The glyph inside the sign — visible between two items, blank after the last. */
export const productBundleJoinMarkVariants = cva('text-primary lap-lg:size-8 size-6 stroke-3', {
  variants: {
    variant: {
      /** Between this line and the next. */
      stated: 'opacity-100',
      /** After the last line, holding the column open and drawing nothing. */
      blank: 'opacity-0',
      unstyled: '',
    },
  },
});

type ProductBundleJoinVariantName = NonNullable<
  VariantProps<typeof productBundleJoinMarkVariants>['variant']
>;

export type ProductBundleJoinVariant = ProductBundleJoinVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductBundleJoinVariant = {
  STATED: 'stated',
  BLANK: 'blank',
  /**
   * The opt-out: a tenant deciding for itself when the sign shows. Carried as
   * an empty entry rather than left out, so what it paints — nothing — is
   * stated in the list instead of decided by absence from it.
   */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductBundleJoinVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductBundleJoinVariantProps = { readonly variant: ProductBundleJoinVariant };

export type ProductBundleJoinProps = ComponentProps<'div'> & ProductBundleJoinVariantProps;

/** The sign between two things in the offer. */
export const ProductBundleJoin = ({ className, variant, ...props }: ProductBundleJoinProps) => (
  <div
    data-slot="product-bundle-join"
    className={clsxm(productBundleJoinClasses, className)}
    {...props}
  >
    <Plus
      data-slot="product-bundle-join-mark"
      className={productBundleJoinMarkVariants({ variant })}
    />
  </div>
);

/**
 * One thing in the offer.
 *
 * This element is the container every step inside the card resolves against —
 * structure a composition cannot see, and load-bearing when missing: a body
 * composed without it would still render, with each `@[220px]` step silently
 * measuring whatever container happened to be above it. It carries no padding
 * of its own for the same reason, so the steps compare a card's full width
 * rather than a width the padding already took from.
 */
const productBundleCardClasses = '@container';

export type ProductBundleCardProps = ComponentProps<'div'>;

/** A thing in the offer, and the measuring box its own steps read. */
export const ProductBundleCard = ({ className, ...props }: ProductBundleCardProps) => (
  <div
    data-slot="product-bundle-card"
    className={clsxm(productBundleCardClasses, className)}
    {...props}
  />
);

/**
 * The card's own box.
 *
 * Whether the thing is in the offer is an outline rather than a word, so it is
 * a state of this box rather than a part of its own. Both states are named
 * arms: what an unselected card paints — the neutral edge — is as much a
 * decision as what a selected one paints.
 */
export const productBundleCardBodyVariants = cva(
  'border-foreground/20 bg-background relative z-10 flex h-full @[220px]:gap-5 @[220px]:p-4 ' +
    'flex-col gap-3 rounded-lg border p-2.5',
  {
    variants: {
      selected: {
        true: 'border-primary',
        false: '',
      },
      disabled: {
        true: 'opacity-75',
        false: '',
      },
    },
  }
);

export type ProductBundleCardBodyProps = ComponentProps<'div'> & {
  /** Whether this thing is part of what the shopper is about to buy. */
  readonly selected: boolean;
  /** Whether the shopper may change that. */
  readonly disabled: boolean;
};

/** The card's box. */
export const ProductBundleCardBody = ({
  className,
  selected,
  disabled,
  ...props
}: ProductBundleCardBodyProps) => (
  <div
    data-slot="product-bundle-card-body"
    className={clsxm(productBundleCardBodyVariants({ selected, disabled }), className)}
    {...props}
  />
);

/**
 * The control that takes a thing in or out of the offer.
 *
 * Square-cornered and unbordered over the card's own corner, which is what
 * makes it read as part of the card rather than as a form field dropped on it.
 * Composed over `@vs-shop/ui`'s checkbox, so it inherits the primitive's mark,
 * its focus ring and its disabled state; what this adds is where it sits.
 */
export const productBundleCardCheckVariants = cva(
  'data-[unchecked]:bg-background-intense-more absolute top-2.5 @[220px]:top-4 @[220px]:right-4 ' +
    'right-2.5 z-10 size-7 rounded-none border-none',
  {
    variants: {
      disabled: {
        true: '',
        false: 'cursor-pointer',
      },
    },
  }
);

export type ProductBundleCardCheckProps = ComponentProps<typeof Checkbox> & {
  /** Whether the shopper may change this line. */
  readonly disabled: boolean;
};

/** The card's in-or-out control. */
export const ProductBundleCardCheck = ({
  className,
  disabled,
  ...props
}: ProductBundleCardCheckProps) => (
  <Checkbox
    data-slot="product-bundle-card-check"
    disabled={disabled}
    className={clsxm(productBundleCardCheckVariants({ disabled }), className)}
    {...props}
  />
);

/**
 * The frame the card's picture hangs in.
 *
 * A fixed aspect with a ceiling per width rather than a fixed height: the cards
 * stand side by side, so their pictures have to agree on a baseline whatever
 * each one's crop is. The frame is usually the way through to the product, so
 * it takes `render` — a tenant with nothing to link to composes the element it
 * wants instead.
 */
const productBundleCardMediaClasses =
  'tab:max-h-[150px] lap:max-h-[195px] flex aspect-4/3 max-h-[118px] w-full items-center ' +
  'justify-center';

export type ProductBundleCardMediaProps = ComponentProps<'div'> & {
  /** The element to paint instead of the default `<div>` — usually a link. */
  readonly render?: RenderElement;
};

/** The picture's frame. */
export const ProductBundleCardMedia = ({
  className,
  render,
  ...props
}: ProductBundleCardMediaProps) => (
  <RenderedPart
    dataSlot="product-bundle-card-media"
    className={clsxm(productBundleCardMediaClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * The card's picture.
 *
 * `src` is final and renderable — CDN/Liip resolution stays app-side — and
 * there is no `sources`, because a card framing one crop is not art-directed.
 * Contained rather than cropped: these are products against a ground, and a
 * crop that filled the frame would cut them.
 */
const productBundleCardImageClasses = 'h-full w-full object-contain';

export type ProductBundleCardImageProps = Omit<VsImageProps, 'className' | 'fill'> & {
  readonly className?: ClassValue;
};

/** The thing's picture. */
export const ProductBundleCardImage = ({ className, ...props }: ProductBundleCardImageProps) => (
  <Image
    data-slot="product-bundle-card-image"
    {...props}
    className={clsxm(productBundleCardImageClasses, className)}
  />
);

/**
 * What is known about the thing.
 *
 * The price is pushed to the bottom rather than following the name, so a column
 * of cards states its prices on one line however long each name runs.
 */
const productBundleCardCopyClasses =
  'flex h-full w-full flex-col justify-between gap-2 @[220px]:gap-3';

export type ProductBundleCardCopyProps = ComponentProps<'div'>;

/** The card's copy column. */
export const ProductBundleCardCopy = ({ className, ...props }: ProductBundleCardCopyProps) => (
  <div
    data-slot="product-bundle-card-copy"
    className={clsxm(productBundleCardCopyClasses, className)}
    {...props}
  />
);

/** The top of the copy column: why the thing is offered, then what it is. */
const productBundleCardHeaderClasses = 'flex flex-col gap-2';

export type ProductBundleCardHeaderProps = ComponentProps<'div'>;

/** The card's naming block. */
export const ProductBundleCardHeader = ({ className, ...props }: ProductBundleCardHeaderProps) => (
  <div
    data-slot="product-bundle-card-header"
    className={clsxm(productBundleCardHeaderClasses, className)}
    {...props}
  />
);

/**
 * Why the thing is in the offer — "recommended", "the pair", a promotion's
 * name. Muted and spaced out, so it reads as a note above the name rather than
 * as part of it.
 */
const productBundleCardLabelClasses = 'text-foreground-muted text-xs/4 tracking-widest';

export type ProductBundleCardLabelProps = ComponentProps<'div'>;

/** The card's reason line. */
export const ProductBundleCardLabel = ({ className, ...props }: ProductBundleCardLabelProps) => (
  <div
    data-slot="product-bundle-card-label"
    className={clsxm(productBundleCardLabelClasses, className)}
    {...props}
  />
);

/**
 * What the thing is.
 *
 * Capped at three lines, because a bundle's cards stand in one row and one long
 * name would set the height of all of them. Takes `render` for the same reason
 * the frame does: this is usually the way through to the product.
 */
const productBundleCardNameClasses = 'line-clamp-3 text-sm/4 hover:underline @[220px]:text-base/5';

export type ProductBundleCardNameProps = ComponentProps<'div'> & {
  /** The element to paint instead of the default `<div>` — usually a link. */
  readonly render?: RenderElement;
};

/** The thing's name. */
export const ProductBundleCardName = ({
  className,
  render,
  ...props
}: ProductBundleCardNameProps) => (
  <RenderedPart
    dataSlot="product-bundle-card-name"
    className={clsxm(productBundleCardNameClasses, className)}
    render={render}
    {...props}
  />
);

/** What the thing costs, formatted by the tenant and stated at the card's foot. */
const productBundleCardPriceClasses = 'text-lg/5 font-semibold @[220px]:text-xl/6';

export type ProductBundleCardPriceProps = ComponentProps<'div'>;

/** The thing's price. */
export const ProductBundleCardPrice = ({ className, ...props }: ProductBundleCardPriceProps) => (
  <div
    data-slot="product-bundle-card-price"
    className={clsxm(productBundleCardPriceClasses, className)}
    {...props}
  />
);

/**
 * Where the offer comes out.
 *
 * The last column of the row, narrower than a card because it holds words and a
 * control rather than a picture — and no wider than the narrowest card, so the
 * row does not break at a width the cards still fit.
 */
const productBundleSummaryClasses = 'lap:max-w-[220px] lap:min-w-[var(--item-min-w)] w-full';

export type ProductBundleSummaryProps = ComponentProps<'div'>;

/** The summary's column. */
export const ProductBundleSummary = ({ className, ...props }: ProductBundleSummaryProps) => (
  <div
    data-slot="product-bundle-summary"
    className={clsxm(productBundleSummaryClasses, className)}
    {...props}
  />
);

/** What the summary holds: what it comes to, then the way into the cart. */
const productBundleSummaryStackClasses = 'flex flex-col gap-6';

export type ProductBundleSummaryStackProps = ComponentProps<'div'>;

/** The summary's stack. */
export const ProductBundleSummaryStack = ({
  className,
  ...props
}: ProductBundleSummaryStackProps) => (
  <div
    data-slot="product-bundle-summary-stack"
    className={clsxm(productBundleSummaryStackClasses, className)}
    {...props}
  />
);

/** The lines the sum is stated over — the count, then the money. */
const productBundleSummaryLinesClasses = 'flex flex-col gap-2';

export type ProductBundleSummaryLinesProps = ComponentProps<'div'>;

/** The summary's lines. */
export const ProductBundleSummaryLines = ({
  className,
  ...props
}: ProductBundleSummaryLinesProps) => (
  <div
    data-slot="product-bundle-summary-lines"
    className={clsxm(productBundleSummaryLinesClasses, className)}
    {...props}
  />
);

export type ProductBundleSummaryNoteProps = ComponentProps<'div'>;

/**
 * What is being added up — how many things, in the tenant's own words and
 * plural rules.
 *
 * No recipe: the line reads at the section's own size, and the emphasis inside
 * it belongs to the sentence rather than to this element, so it arrives as
 * children already marked up.
 */
export const ProductBundleSummaryNote = ({
  className,
  ...props
}: ProductBundleSummaryNoteProps) => (
  <div
    data-slot="product-bundle-summary-note"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/** What the offer comes to. The largest thing in the column, and the point of it. */
const productBundleSummaryTotalClasses = 'text-2xl font-bold';

export type ProductBundleSummaryTotalProps = ComponentProps<'div'>;

/** The offer's total. */
export const ProductBundleSummaryTotal = ({
  className,
  ...props
}: ProductBundleSummaryTotalProps) => (
  <div
    data-slot="product-bundle-summary-total"
    className={clsxm(productBundleSummaryTotalClasses, className)}
    {...props}
  />
);
